// src/pages/admin/AdminStats.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  addDoc, 
  deleteDoc, 
  doc,
  updateDoc,
  where 
} from 'firebase/firestore';
import { db } from '../../firebase/firestore';
import { 
  MousePointer, 
  Users, 
  Copy, 
  Trash2, 
  Plus, 
  X,
  TrendingUp,
  CheckCircle,
  XCircle,
  CreditCard,
  Download,
  Calendar,
  Filter
} from 'lucide-react';

const AdminStats = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [stats, setStats] = useState({
    totalClicks: 0,
    totalCampaigns: 0,
    uniqueUsers: 0,
    totalPurchases: 0,
    totalRevenue: 0,
    conversionRate: 0
  });
  
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    source: '',
    medium: '',
    campaign: '',
    utmUrl: ''
  });

  // Telegram WebApp инициализация
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      window.Telegram.WebApp.BackButton.isVisible = true;
      window.Telegram.WebApp.BackButton.onClick(() => {
        navigate(-1);
      });
    }

    window.scrollTo(0, 0);

    return () => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.BackButton.isVisible = false;
        window.Telegram.WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  // Загрузка данных
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 1. Загружаем кампании
      const campaignsRef = collection(db, 'analytics_campaigns');
      const campaignsQuery = query(campaignsRef, orderBy('createdAt', 'desc'));
      const campaignsSnapshot = await getDocs(campaignsQuery);
      
      const campaignsData = [];
      campaignsSnapshot.forEach(doc => {
        campaignsData.push({ id: doc.id, ...doc.data() });
      });
      setCampaigns(campaignsData);

      // 2. Загружаем клики
      const clicksRef = collection(db, 'analytics_clicks');
      const clicksQuery = query(clicksRef, orderBy('timestamp', 'desc'));
      const clicksSnapshot = await getDocs(clicksQuery);
      
      const clicksData = [];
      clicksSnapshot.forEach(doc => {
        clicksData.push({ id: doc.id, ...doc.data() });
      });
      setClicks(clicksData);

      // 3. Загружаем платежи из landing_payments
      const paymentsRef = collection(db, 'landing_payments');
      const paymentsQuery = query(paymentsRef, orderBy('createdAt', 'desc'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      const paymentsData = [];
      const purchasesByUser = new Map(); // Для подсчета уникальных покупателей
      
      paymentsSnapshot.forEach(doc => {
        const payment = { id: doc.id, ...doc.data() };
        paymentsData.push(payment);
        
        // Считаем уникальных покупателей
        if (payment.customerEmail) {
          purchasesByUser.set(payment.customerEmail, true);
        }
      });
      setPayments(paymentsData);

      // 4. Обновляем статистику кампаний
      await updateCampaignStats(campaignsData, clicksData, paymentsData);

      // 5. Общая статистика
      const uniqueUsers = new Set();
      clicksData.forEach(click => {
        if (click.userId) uniqueUsers.add(click.userId);
      });

      const totalRevenue = paymentsData.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalPurchases = paymentsData.length;

      setStats({
        totalClicks: clicksData.length,
        totalCampaigns: campaignsData.length,
        uniqueUsers: uniqueUsers.size,
        totalPurchases: totalPurchases,
        totalRevenue: totalRevenue,
        conversionRate: clicksData.length ? ((totalPurchases / clicksData.length) * 100).toFixed(1) : 0
      });

    } catch (error) {
      console.error('Ошибка загрузки:', error);
    } finally {
      setLoading(false);
    }
  };

  // Обновление статистики кампаний на основе реальных данных
  const updateCampaignStats = async (campaignsData, clicksData, paymentsData) => {
    try {
      for (const campaign of campaignsData) {
        if (!campaign.campaignId) continue;

        // Считаем клики по этой кампании
        const campaignClicks = clicksData.filter(click => 
          click.raw_param === campaign.campaignId || 
          click.campaignId === campaign.campaignId
        );

        // Находим уникальных пользователей
        const uniqueUsers = new Set();
        campaignClicks.forEach(click => {
          if (click.userId) uniqueUsers.add(click.userId);
        });

        // Находим покупки от этих пользователей
        let campaignPurchases = 0;
        let campaignRevenue = 0;

        // Ищем платежи, которые могли быть от пользователей из этой кампании
        // Это сложно напрямую связать, поэтому используем приблизительный подсчет
        // или обновляем вручную при создании платежа с metadata кампании

        // Обновляем статистику кампании в Firebase
        const campaignRef = doc(db, 'analytics_campaigns', campaign.id);
        await updateDoc(campaignRef, {
          clicks: campaignClicks.length,
          uniqueUsers: uniqueUsers.size,
          purchases: campaignPurchases,
          revenue: campaignRevenue,
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Ошибка обновления статистики кампаний:', error);
    }
  };

  // Генерация короткой ссылки
  const generateCampaignLink = () => {
    if (!newCampaign.name) return;
    
    // Создаем короткий ID из названия (транслит)
    const campaignId = 'camp_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    const link = `https://t.me/innerart_bot?start=${campaignId}`;
    
    setNewCampaign(prev => ({
      ...prev,
      utmUrl: link,
      campaignId: campaignId
    }));
  };

  // Создание кампании
  const createCampaign = async () => {
    try {
      const campaignData = {
        name: newCampaign.name,
        source: newCampaign.source || 'direct',
        medium: newCampaign.medium || 'none',
        campaign: newCampaign.campaign || '',
        campaignId: newCampaign.campaignId,
        link: newCampaign.utmUrl,
        clicks: 0,
        uniqueUsers: 0,
        purchases: 0,
        revenue: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active'
      };

      await addDoc(collection(db, 'analytics_campaigns'), campaignData);

      setShowCreateModal(false);
      setNewCampaign({
        name: '',
        source: '',
        medium: '',
        campaign: '',
        utmUrl: ''
      });
      
      loadData();
    } catch (error) {
      console.error('Ошибка создания кампании:', error);
    }
  };

  // Удаление кампании
  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Удалить кампанию?')) return;
    try {
      await deleteDoc(doc(db, 'analytics_campaigns', campaignId));
      loadData();
    } catch (error) {
      console.error('Ошибка удаления:', error);
    }
  };

  // Копирование ссылки
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Ссылка скопирована!');
  };

  // Экспорт в CSV
  const exportToCSV = () => {
    let csv = 'Дата,Пользователь,Email,Кампания,Сумма,Валюта,Статус,Session ID\n';
    
    // Экспортируем платежи
    payments.forEach(p => {
      csv += `"${new Date(p.createdAt).toLocaleString()}","${p.userId || '—'}","${p.customerEmail || '—'}","${p.courseName || '—'}",${p.amount || 0},${p.currency || 'eur'},${p.paymentStatus},${p.sessionId}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Фильтрация по дате
  const filterByDate = (items, dateField) => {
    if (dateFilter === 'all') return items;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekAgo = today - 7 * 24 * 60 * 60 * 1000;
    const monthAgo = today - 30 * 24 * 60 * 60 * 1000;
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField]).getTime();
      if (dateFilter === 'today') return itemDate >= today;
      if (dateFilter === 'week') return itemDate >= weekAgo;
      if (dateFilter === 'month') return itemDate >= monthAgo;
      return true;
    });
  };

  const filteredPayments = filterByDate(payments, 'createdAt');
  const filteredClicks = filterByDate(clicks, 'timestamp');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center text-white">
        <div className="relative z-10 animate-pulse">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#0A0A0F] text-white relative">
      {/* Градиентные акценты */}
      <div className="fixed top-0 left-0 right-0 h-[300px] bg-gradient-to-b from-[#1A0F2E]/50 to-transparent pointer-events-none" />
      <div className="fixed bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t from-[#1A0F2E]/50 to-transparent pointer-events-none" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="pt-4 px-4">
          <h1 className="text-white text-2xl font-bold">Аналитика</h1>
        </div>
        
        <div className="flex-1 px-4 pt-2 pb-24 overflow-y-auto">
          {/* Фильтр по дате */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
            <Calendar className="w-4 h-4 text-white/40" />
            <button
              onClick={() => setDateFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                dateFilter === 'all' ? 'bg-[#C8AFE8] text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              Все время
            </button>
            <button
              onClick={() => setDateFilter('today')}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                dateFilter === 'today' ? 'bg-[#C8AFE8] text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              Сегодня
            </button>
            <button
              onClick={() => setDateFilter('week')}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                dateFilter === 'week' ? 'bg-[#C8AFE8] text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              Неделя
            </button>
            <button
              onClick={() => setDateFilter('month')}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                dateFilter === 'month' ? 'bg-[#C8AFE8] text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              Месяц
            </button>
          </div>

          {/* Табы */}
          <div className="flex gap-4 mb-6 border-b border-white/10">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`pb-3 px-1 text-base font-medium transition-colors ${
                activeTab === 'campaigns' ? 'text-[#C8AFE8] border-b-2 border-[#C8AFE8]' : 'text-white/50 hover:text-white/70'
              }`}
            >
              Кампании
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`pb-3 px-1 text-base font-medium transition-colors ${
                activeTab === 'payments' ? 'text-[#C8AFE8] border-b-2 border-[#C8AFE8]' : 'text-white/50 hover:text-white/70'
              }`}
            >
              Платежи
            </button>
            <button
              onClick={() => setActiveTab('clicks')}
              className={`pb-3 px-1 text-base font-medium transition-colors ${
                activeTab === 'clicks' ? 'text-[#C8AFE8] border-b-2 border-[#C8AFE8]' : 'text-white/50 hover:text-white/70'
              }`}
            >
              Клики
            </button>
          </div>

          {/* Статистика */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
              <MousePointer className="w-5 h-5 text-[#34D3FF] mb-2" />
              <div className="text-white/60 text-sm">Клики</div>
              <div className="text-2xl font-bold">{filteredClicks.length}</div>
            </div>
            <div className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
              <Users className="w-5 h-5 text-[#34D3FF] mb-2" />
              <div className="text-white/60 text-sm">Уникальные</div>
              <div className="text-2xl font-bold">{stats.uniqueUsers}</div>
            </div>
            <div className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
              <CreditCard className="w-5 h-5 text-[#34D3FF] mb-2" />
              <div className="text-white/60 text-sm">Покупки</div>
              <div className="text-2xl font-bold">{filteredPayments.length}</div>
            </div>
            <div className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
              <TrendingUp className="w-5 h-5 text-[#34D3FF] mb-2" />
              <div className="text-white/60 text-sm">Выручка</div>
              <div className="text-2xl font-bold">{filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0)}€</div>
            </div>
          </div>

          {/* Вкладка Кампании */}
          {activeTab === 'campaigns' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-[#6C6FF8] to-[#F466C8] rounded-xl text-white text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  Новая кампания
                </button>
              </div>

              {campaigns.map(campaign => (
                <div key={campaign.id} className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-[#C8AFE8] font-medium text-lg">{campaign.name}</h3>
                      <div className="flex gap-2 mt-1 text-sm text-white/60">
                        <span>📱 {campaign.source}</span>
                        <span>📊 {campaign.medium}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#34D3FF] font-bold">{campaign.clicks || 0}</span>
                      <span className="text-white/60 text-sm">кликов</span>
                    </div>
                  </div>

                  <div className="bg-black/40 rounded-xl p-3 mb-3">
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-xs text-[#8D85D5] break-all">{campaign.link}</code>
                      <button
                        onClick={() => copyToClipboard(campaign.link)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4 text-[#34D3FF]" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div className="bg-black/40 rounded-lg p-2">
                      <div className="text-white/40">Переходы</div>
                      <div className="text-white font-bold">{campaign.clicks || 0}</div>
                    </div>
                    <div className="bg-black/40 rounded-lg p-2">
                      <div className="text-white/40">Покупки</div>
                      <div className="text-white font-bold">{campaign.purchases || 0}</div>
                    </div>
                    <div className="bg-black/40 rounded-lg p-2">
                      <div className="text-white/40">Выручка</div>
                      <div className="text-white font-bold">{campaign.revenue || 0}€</div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-3">
                    <button
                      onClick={() => deleteCampaign(campaign.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {campaigns.length === 0 && (
                <div className="text-center py-12 text-white/40 bg-[#1A1A24] rounded-2xl border border-white/10">
                  Нет созданных кампаний
                </div>
              )}
            </div>
          )}

          {/* Вкладка Платежи */}
          {activeTab === 'payments' && (
            <div className="space-y-4">
              <div className="flex justify-end gap-2">
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-white/10 rounded-xl text-white text-sm font-medium flex items-center gap-2 hover:bg-white/20 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Экспорт CSV
                </button>
              </div>

              {filteredPayments.map(payment => (
                <div key={payment.id} className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-white font-medium">{payment.courseName || 'Курс'}</span>
                    </div>
                    <span className="text-[#34D3FF] font-bold">{payment.amount} {payment.currency}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                    <div>
                      <div className="text-white/40">Email</div>
                      <div className="text-white truncate">{payment.customerEmail || '—'}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Дата</div>
                      <div className="text-white">{new Date(payment.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Session ID</div>
                      <div className="text-white text-xs truncate">{payment.sessionId}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Payment Link</div>
                      <div className="text-white text-xs truncate">{payment.paymentLinkId}</div>
                    </div>
                  </div>

                  {payment.isTest && (
                    <div className="mt-2">
                      <span className="inline-block px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-lg">
                        Тестовый платеж
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {filteredPayments.length === 0 && (
                <div className="text-center py-12 text-white/40 bg-[#1A1A24] rounded-2xl border border-white/10">
                  Нет платежей за выбранный период
                </div>
              )}
            </div>
          )}

          {/* Вкладка Клики */}
          {activeTab === 'clicks' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-white/10 rounded-xl text-white text-sm font-medium hover:bg-white/20 transition-colors"
                >
                  Экспорт CSV
                </button>
              </div>

              {filteredClicks.map(click => (
                <div key={click.id} className="bg-[#1A1A24] rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-white font-medium">User: {click.userId || '—'}</span>
                      </div>
                      <div className="text-sm text-white/60">
                        📅 {new Date(click.timestamp).toLocaleString()}
                      </div>
                      <div className="text-sm text-white/60 mt-1">
                        📊 UTM: {click.utm_source || '—'} / {click.utm_medium || '—'}
                      </div>
                      {click.raw_param && (
                        <div className="text-sm text-white/40 mt-1">
                          🔗 Параметр: {click.raw_param}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredClicks.length === 0 && (
                <div className="text-center py-12 text-white/40 bg-[#1A1A24] rounded-2xl border border-white/10">
                  Нет кликов за выбранный период
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно создания кампании */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1A24] rounded-2xl w-full max-w-md border border-white/10">
            <div className="flex justify-between items-center p-4 border-b border-white/10">
              <h2 className="text-white text-lg font-medium">Новая кампания</h2>
              <button 
                onClick={() => setShowCreateModal(false)} 
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Название кампании *</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => {
                    setNewCampaign({ ...newCampaign, name: e.target.value });
                    generateCampaignLink();
                  }}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-white/30 focus:outline-none transition-colors"
                  placeholder="Instagram Stories Март"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-sm mb-1">Источник</label>
                  <input
                    type="text"
                    value={newCampaign.source}
                    onChange={(e) => setNewCampaign({ ...newCampaign, source: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-white/30 focus:outline-none transition-colors"
                    placeholder="instagram"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-1">Канал</label>
                  <input
                    type="text"
                    value={newCampaign.medium}
                    onChange={(e) => setNewCampaign({ ...newCampaign, medium: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-white/30 focus:outline-none transition-colors"
                    placeholder="social"
                  />
                </div>
              </div>

              {newCampaign.utmUrl && (
                <div className="bg-black/40 rounded-xl p-3">
                  <div className="text-sm text-white/60 mb-2">Ссылка для рекламы:</div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-[#8D85D5] break-all flex-1">{newCampaign.utmUrl}</code>
                    <button
                      onClick={() => copyToClipboard(newCampaign.utmUrl)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Copy className="w-4 h-4 text-[#34D3FF]" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-white/10 flex gap-3">
              <button
                onClick={createCampaign}
                disabled={!newCampaign.name}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#6C6FF8] to-[#F466C8] rounded-xl text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                Создать
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-3 bg-white/10 rounded-xl text-white font-medium hover:bg-white/20 transition-colors"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStats;